/** @jsxImportSource preact */
import type { ReplyButton } from '../types';
import type { ActionHandler } from './types';

interface QuickReplyButtonsProps {
  text?: string;
  buttons: ReplyButton[];
  onAction: ActionHandler;
}

/**
 * WhatsApp "reply buttons" (interactive button message). Renders an optional
 * body text as an assistant bubble, then up to 3 full-width, stacked
 * reply-buttons. Tapping a button re-sends its `reply` string as the
 * shopper's next message via a `send-reply` intent.
 *
 * XSS: JSX text interpolation only — Preact escapes labels/text. No
 * dangerouslySetInnerHTML.
 */
export function QuickReplyButtons({ text, buttons, onAction }: QuickReplyButtonsProps) {
  const safeButtons = (Array.isArray(buttons) ? buttons : [])
    .filter((b) => b && typeof b.label === 'string' && b.label.length > 0)
    .slice(0, 3);

  if (!text && safeButtons.length === 0) return null;

  return (
    <div class="lc-message assistant lc-qr">
      {text ? <div class="lc-message-bubble lc-qr-text">{text}</div> : null}
      {safeButtons.length > 0 ? (
        <div class="lc-qr-buttons" role="group">
          {safeButtons.map((button) => (
            <button
              key={button.id || button.reply}
              type="button"
              class="lc-qr-button"
              data-reply-id={button.id || ''}
              onClick={() => onAction({ kind: 'send-reply', text: button.reply })}
            >
              {button.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
